import { Formik, Form } from 'formik'
import styled from 'styled-components'
import { mergeRight } from 'ramda'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Row, Group, FieldInput, FieldDropdown, PhoneInput } from '@/components/form'
import { ZipCodeInput, StateDropdown } from '@/components/address-form'
import useBasic from '@/hooks/use-basic'
import useMediaQuery from '@/hooks/use-media-query'
import { getFormattedCountries } from '@/utils/address'
import Page from '@/components/page'
import { toUnit } from '@/utils/styles'
import { breakpoints, structure } from '@/styles'
import '@/utils/validation'
import PadButtonBar from './pad-button-bar'
import { StyledContentWrapper, StyledPanel, StyledAreaTitle } from './elements'
import Context from './context'

const StyledForm = styled(Form)`
  overflow-y: scroll;
  height: calc(100vh - ${toUnit(structure.header.height * 2)});
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)});
    height: unset;
    overflow-y: unset;
  }
`

const PersonalInfo = ({ setCurrentStep, handleBack, handleDelete, isModal }) => {
  const { t } = useTranslation()
  const { formData, setFormData } = useContext(Context)
  const displayDesktopSmall = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const colSize = displayDesktopSmall ? 12 : 6
  const { countries } = useBasic(['countries'])
  const formattedCountries = getFormattedCountries(countries)

  const initialValues = {
    first_name: formData?.first_name || '',
    last_name: formData?.last_name || '',
    phone_number: formData?.phone_number || '',
    email: formData?.email || '',
    company_name: formData?.company_name || '',
    street1: formData?.street1 || '',
    street2: formData?.street2 || '',
    city: formData?.city || '',
    zip: formData?.zip || '',
    country: formData?.country || 'ca',
    state: formData?.state || ''
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().min(2).required(),
    last_name: Yup.string().min(2).required(),
    phone_number: Yup.string().phone().required(),
    email: Yup.string().email().required(),
    company_name: Yup.string(),
    street1: Yup.string().min(2).required(),
    street2: Yup.string(),
    city: Yup.string().min(2).required(),
    zip: Yup.string().when(['country'], (country, schema) => {
      if (country === 'ca') {
        return schema.length(7).required()
      }
      if (country === 'us') {
        return schema.length(5).required()
      }
      return schema.required()
    }),
    country: Yup.string().required(),
    state: Yup.string().required()
  })

  const onSubmit = async (values, { setSubmitting }) => {
    setFormData(mergeRight(formData, values))
    setSubmitting(false)
    setCurrentStep('financial_info')
    window.scrollTo(0, 0)
  }

  const countryDropDown = (setValue, countryName, stateName) => (
    <Group col={colSize}>
      <FieldDropdown
        id={countryName}
        name={countryName}
        options={formattedCountries}
        onChange={() => setValue(stateName, '')}
        placeholder={t('l.payment_settings.edit.country.label', 'Country')}
        isSearchable
      />
    </Group>
  )

  const provinceDropDown = (formValues, countryName, stateName) => (
    <Group col={colSize}>
      <StateDropdown country={formValues[countryName]} id={stateName} name={stateName} required />
    </Group>
  )

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, handleChange, isSubmitting }) => (
        <StyledForm className="flex-full">
          <Page>
            <StyledContentWrapper isModal={isModal}>
              <StyledAreaTitle isModal={isModal}>
                {t('r.payment_settings.edit.bank_account_info.subtitle', 'Bank Account Info')}
              </StyledAreaTitle>
              <StyledPanel isModal={isModal}>
                <Row>
                  <Group col={colSize}>
                    <FieldInput
                      id="first_name"
                      name="first_name"
                      placeholder={t('l.payment_settings.edit.first_name.label', 'First name')}
                      required
                      disabled
                    />
                  </Group>
                  <Group col={colSize}>
                    <FieldInput
                      id="last_name"
                      name="last_name"
                      placeholder={t('l.payment_settings.edit.last_name.label', 'Last name')}
                      required
                      disabled
                    />
                  </Group>
                </Row>
                <Row>
                  <Group col={colSize}>
                    <PhoneInput
                      id="phone_number"
                      name="phone_number"
                      value={values.phone_number}
                      onChange={handleChange('phone_number')}
                      bordered
                      showFlag={false}
                      placeholder={t('l.payment_settings.edit.phone_number.label', 'Phone number')}
                      required
                    />
                  </Group>
                  <Group col={colSize}>
                    <FieldInput
                      id="email"
                      name="email"
                      placeholder={t('l.payment_settings.edit.email.label', 'Email')}
                      required
                    />
                  </Group>
                </Row>
                <Row>
                  <Group col={12}>
                    <FieldInput
                      id="company_name"
                      name="company_name"
                      placeholder={t(
                        'l.payment_settings.edit.company_name_optional.label',
                        'Company name (optional)'
                      )}
                    />
                  </Group>
                </Row>
                <Row>
                  <Group col={colSize}>
                    <FieldInput
                      id="street1"
                      name="street1"
                      placeholder={t(
                        'l.payment_settings.edit.street_address.label',
                        'Street address'
                      )}
                      required
                    />
                  </Group>
                  <Group col={colSize}>
                    <FieldInput
                      id="street2"
                      name="street2"
                      placeholder={t('l.payment_settings.edit.unit_number.label', 'Unit number')}
                    />
                  </Group>
                </Row>
                <Row>
                  <Group col={colSize}>
                    <FieldInput
                      id="city"
                      name="city"
                      placeholder={t('l.payment_settings.edit.city.label', 'City')}
                      required
                    />
                  </Group>
                  <Group col={colSize}>
                    <ZipCodeInput country={values.country} required />
                  </Group>
                </Row>
                <Row>
                  {countryDropDown(setFieldValue, 'country', 'state')}
                  {provinceDropDown(values, 'country', 'state')}
                </Row>
              </StyledPanel>
            </StyledContentWrapper>
          </Page>
          <PadButtonBar
            isModal={isModal}
            handleBack={handleBack}
            handleDelete={handleDelete}
            disabled={isSubmitting}
            primaryButtonText={t('common.next', 'Next')}
          />
        </StyledForm>
      )}
    </Formik>
  )
}

export default PersonalInfo
